import { PromotionCardVariant } from '@purpur/library';
import { match } from 'ts-pattern';

import { BlockPromoType, CardType } from '../../models/Content';
import { transformCta, transformResponsiveImage } from '../../utils/transformers';
import { BlockPromoBackground, BlockPromoData, BlockPromoDisplayVariant, PromoCardData } from './model';

const transformPromoCard = (
    card: CardType,
    blockBackgroundColor: BlockPromoBackground,
    index: number,
): PromoCardData => {
    if (!card) {
        return undefined;
    }

    const defaultCardVariant = match<BlockPromoBackground, PromotionCardVariant>(blockBackgroundColor)
        .with('light', () => 'primary')
        .with('dark', () => 'secondary')
        .with('black', () => 'quaternary')
        .otherwise(() => undefined);

    const colorVariant = match<CardType['fields']['colorOption'], PromotionCardVariant>(card.fields.colorOption)
        .with('color-dark-purple', () => 'primary')
        .with('color-light-purple', () => 'secondary')
        .with('color-beige', () => 'quaternary')
        .otherwise(() => undefined);

    return {
        id: `${card.sys.id}-${index}`,
        variant: colorVariant || defaultCardVariant || 'primary',
        title: card.fields.title,
        badge: card.fields.badge,
        badgeIcon: card.fields.badgeIcon,
        description: card.fields.description,
        descriptionList: card.fields.list,
        imageContainerReference: card.fields.imageContainerReference,
        media: transformResponsiveImage(card.fields.media),
        ctas: card.fields.cta?.map(transformCta),
        contentSlot: card.fields.contentSlot,
        offerSchema: card.fields.offerSchema,
        legalText: card.fields.legalText?.fields.description,
    };
};

export const transformBlockPromo = (blockPromo: BlockPromoType): BlockPromoData => {
    if (!blockPromo) {
        return undefined;
    }

    const backgroundColor = match<BlockPromoType['fields']['colorOption'], BlockPromoBackground>(
        blockPromo.fields.colorOption,
    )
        .with('color-dark-purple', () => 'dark')
        .with('color-light-purple', () => 'light')
        .with('color-black', () => 'black')
        .otherwise(() => undefined);

    const displayVariant = match<BlockPromoType['fields']['displayOption'], BlockPromoDisplayVariant>(
        blockPromo.fields.displayOption,
    )
        .with('1:1 landscape', () => '1-1-landscape')
        .with('1:1 portrait', () => '1-1-portrait')
        .with('1:1:1', () => '1-1-1')
        .with('1:2', () => '1-2')
        .with('1:3', () => '1-3')
        .with('2:2', () => '2-2')
        .otherwise(() => 'standalone');

    const isHeaderNegative = backgroundColor === 'dark' || backgroundColor === 'black';

    return {
        id: blockPromo.sys.id,
        backgroundColor,
        displayVariant,
        isHeaderNegative,
        blockTitle: blockPromo.fields.title,
        blockCta: transformCta(blockPromo.fields.cta),
        cards: blockPromo.fields.cards?.map((card, index) => transformPromoCard(card, backgroundColor, index)),
    };
};
